





















































































  import { defineComponent, ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
  import LazyHydrate from 'vue-lazy-hydration'
  import SeoMethodsMixin, { DIVIDER } from '../mixins/SeoMethodsMixin'
  import { StickyStack } from '~/plugins/stickystack'
  import useCurrentInstance from '~/utils/useCurrentInstance'
  import { useCompareStore } from '~/stores/useCompareStore'
  import OneHidePriceSwitcherComponent from '~/components/organisms/grid/header/OneHidePriceSwitcherComponent.vue'

  interface MetaInfo {
    title: string
    titleTemplate: (titleChunk: string) => string
    htmlAttrs?: {
      lang?: string
      [key: string]: any
    }
    meta: Array<{
      charset?: string
      hid?: string
      name?: string
      content?: string | null
      property?: string
      [key: string]: any
    }>
    link: Array<{
      rel?: string
      type?: string
      href?: string | null
      sizes?: string
      pbody?: boolean
      as?: string
      [key: string]: any
    }>
    script: Array<{
      type?: string
      src?: string
      json?: any
      rel?: string
      body?: boolean
      async?: boolean
      defer?: boolean
      [key: string]: any
    }>
    noscript: Array<{
      innerHTML?: string
      [key: string]: any
    }>
  }

  enum HeadTagsTypes {
    link = 'link',
    script = 'script',
    meta = 'meta',
    noscript = 'noscript',
  }

  export default defineComponent({
    name: 'MainLayout',
    components: {
      OneAvailableNotificationUpdate: () => import('~/components/molecules/notifications/OneAvailableNotificationUpdate.vue'),
      OneHeaderGridComponent: () => import('~/components/organisms/header/OneHeaderGridComponent.vue'),
      OneFooter: () => import('~/components/organisms/footer/OneFooter.vue'),
      OneOfflineNotification: () => import('~/components/molecules/notifications/OneOfflineNotification.vue'),
      OneButtonToggle: () => import('~/components/molecules/buttons/OneButtonToggle.vue'),
      MobileMenuWrapper: () => import('~/components/molecules/menus/MobileMenuWrapper.vue'),
      MobileAccountMenuWrapper: () => import('~/components/molecules/menus/MobileAccountMenuWrapper.vue'),
      OneBreadcrumbs: () => import('~/components/molecules/breadcrumbs/OneBreadcrumbs.vue'),
      OneCookieBanner: () => import('~/components/molecules/banners/OneCookieBanner.vue'),
      OneNoticeBanner: () => import('~/components/molecules/banners/OneNoticeBanner.vue'),
      OneInvoiceBanner: () => import('~/components/molecules/banners/OneInvoiceBanner.vue'),
      OneFullScreenPluginModal: () => import('~/components/molecules/modals/OneFullScreenPluginModal.vue'),
      OneCmsYoutubeComponent: () => import('~/components/organisms/grid/shared/OneCmsYoutubeComponent.vue'),
      LazyHydrate,
      OneHidePriceSwitcherComponent,
    },
    mixins: [SeoMethodsMixin],

    setup() {
      const vm = useCurrentInstance()
      const compareStore = useCompareStore()
      const DEFAULT_NOTIF_DURATION = 6000
      const updateCallback = ref<Function | null>(null)
      let stack: StickyStack | null = null
      const isHovered = ref(false)

      const tenantKey = computed(() => vm.$store.state.tenantKey)
      const menuAccountShow = computed(() => vm.$store.state.layout.menuAccountShow)
      const menuMobileShow = computed(() => vm.$store.state.layout.menuMobileShow)
      const menuFiltersShow = computed(() => vm.$store.state.layout.menuFiltersShow)
      const breadcrumbs = computed(() => vm.$store.state.layout.breadcrumbs)
      const showBreadcrumbs = computed(() => vm.$store.state.layout.showBreadcrumbs)
      const backdrop = computed(() => vm.$store.state.layout.backdrop)
      const isPriceGross = computed(() => vm.$store.state.layout.priceGross)
      const isIdle = computed(() => vm.$store.state.idle.isIdle)

      const configuration = computed(() => vm.$store.getters['cms/configuration'])
      const logo = computed(() => vm.$store.getters['cms/logo'])
      const getClient = computed(() => vm.$store.getters['account/getClient'])

      const fetchHeavyData = () => vm.$store.dispatch('fetchHeavyData')
      const toggleMenuAccount = (val: boolean) => vm.$store.commit('layout/TOGGLE_ACCOUNT_MENU', val)
      const toggleMobileMenu = (val: boolean) => vm.$store.commit('layout/TOGGLE_MOBILE_MENU', val)
      const setPriceGross = (val: boolean) => vm.$store.commit('layout/SET_PRICE_GROSS', val)
      const backdropRun = (val: boolean) => vm.$store.commit('layout/TOGGLE_BACKDROP', val)

      const isMobileOrTablet = computed(() => vm.$utils.isMobileOrTablet(vm.$mq))

      const logoData = computed(() => configuration.value?.logo?.img)

      const backdropCategory = computed(() => ({
        booleanValue: backdrop.value,
        uid: 'category',
      }))

      const comparedProducts = computed(() => compareStore.products)
      const compareCount = computed(() => compareStore.compareCount)

      const defaultSeoTitleTemplate = (titleChunk: string): string | null => {
        return titleChunk || (vm as any).defaultSeoData.title
      }

      const onUpdateAnswer = (accepted: boolean) => {
        if (updateCallback.value) {
          updateCallback.value(accepted)
          updateCallback.value = null
        }
      }

      const showUpdatePrompt = (cb: Function) => {
        updateCallback.value = cb
        vm.$notify({
          group: 'pwaUpdate',
          duration: -1,
        })
      }

      const initializeStickyStack = () => {
        nextTick(() => {
          stack = new StickyStack()
        })
      }

      const onCategoryClick = (categoryId: string | null) => {
        if (categoryId) {
          const categoryPath: string | null = vm.$routes.getCategoryPath(categoryId)
          if (categoryPath) {
            vm.$router.push(categoryPath, () => {
              toggleMobileMenu(false)
            })
          }
        }
      }

      const menuAccountClose = () => {
        if (menuAccountShow.value) {
          toggleMenuAccount(false)
        }
      }

      const closeMobileMenuAfterSelected = () => {
        if (process.client) {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        menuAccountClose()
      }

      const setHeadTags = (header: MetaInfo) => {
        try {
          const headTags = configuration.value?.globalTags?.tagsHeadHtml
          if (headTags == null || JSON.parse(headTags)?.tags == null) return

          const tagsObject = JSON.parse(headTags)
          if (tagsObject.tags == null) return

          const tenantScriptsOff = vm.$route.query?.tenantScriptsOff === 'true'

          tagsObject.tags.forEach(({ type, attributes }: { type: HeadTagsTypes; attributes: any }) => {
            if (type === 'script' && tenantScriptsOff) return
            header[type] = header[type] ? [...header[type], attributes] : [attributes]
          })
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setHeadTags]', error)
        }
      }

      const setLangAttribute = (header: MetaInfo) => {
        try {
          if (vm.$tenantConfig.configuration?.lang) {
            header.htmlAttrs = { lang: vm.$tenantConfig.configuration.lang }
          }
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setLangAttribute]', error)
        }
      }

      const setTenantIcons = (header: MetaInfo) => {
        try {
          if (vm.$tenantConfig.configuration?.theme.themeIcons) {
            header.link.push({
              rel: 'stylesheet',
              href: vm.$tenantConfig.configuration.theme.themeIcons.css,
              pbody: true, // Custom attribute
            } as any)
          }
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setTenantIcons]', error)
        }
      }
      const setTenantCss = (header: MetaInfo) => {
        try {
          const config = vm.$tenantConfig.configuration as any

          if (config?.globalCssUrl) {
            header.link.push({
              rel: 'stylesheet',
              href: config.globalCssUrl,
              pbody: true,
            } as any)
          }
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setTenantCss]', error)
        }
      }

      const setScripts = (header: MetaInfo) => {
        try {
          const config = vm.$tenantConfig.configuration as any
          const globalScripts = config?.globalScripts
          const tenantScriptsOff = vm.$route.query?.tenantScriptsOff === 'true'

          if (!globalScripts || tenantScriptsOff) return

          const headerScript = globalScripts.scriptsHeadUrl
          const bodyScript = globalScripts.scriptsBodyUrl
          const { async, defer } = globalScripts

          header.script.push({ rel: 'script', body: false, src: headerScript, async, defer })
          header.script.push({ rel: 'script', body: true, src: bodyScript, async, defer })
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setScripts]', error)
        }
      }

      const setPwaAttributes = (header: MetaInfo) => {
        try {
          if (vm.$pwa?.isEnabled) {
            header.link = [
              ...header.link,
              { rel: 'manifest', href: '/api/manifest.json' },
              { rel: 'apple-touch-icon', href: vm.$pwa.appleIconUrl, sizes: '512x512' },
            ]
            header.meta.push({
              name: 'theme-color',
              content: vm.$lib.primary,
            })
          }
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setPwaAttributes]', error)
        }
      }

      watch(isIdle, (newVal) => {
        if (!newVal && vm.$auth.isAuthenticated) {
          vm.$api.webhooks.app.publishClientLogged({
            externalId: getClient.value.externalId,
          })
        }
      })

      watch(menuFiltersShow, (newVal) => {
        if (process.client) {
          const layoutOverflow = newVal ? 'hidden' : 'unset'
          const layoutEl = document.getElementById('__layout')
          if (layoutEl) layoutEl.style.overflowX = layoutOverflow
        }
      }, { immediate: true })

      watch(menuMobileShow, (newVal) => {
        if (process.client) {
          const layoutOverflow = newVal ? 'hidden' : 'unset'
          const layoutEl = document.getElementById('__layout')
          if (layoutEl) layoutEl.style.overflowX = layoutOverflow
        }
      }, { immediate: true })

      watch(() => vm.$route.path, (newPath) => {
        // @EnsureClientSide logic
        if (process.client) {
          document.body.className = newPath.substring(1).replace(/\//g, '-').replace(/-\s*$/, '') || 'index'
        }
      }, { immediate: true })

      onMounted(async () => {
        if (process.client) {
          if (vm.$pwa) vm.$pwa.$on('pwa:update:prompt', showUpdatePrompt)
          initializeStickyStack()
          await fetchHeavyData()
        }
      })

      onUnmounted(() => {
        if (process.client && vm.$pwa) {
          vm.$pwa.$off('pwa:update:prompt', showUpdatePrompt)
        }
      })

      return {
        DEFAULT_NOTIF_DURATION,
        isHovered,
        tenantKey,
        menuAccountShow,
        menuMobileShow,
        menuFiltersShow,
        breadcrumbs,
        showBreadcrumbs,
        backdrop,
        isPriceGross,
        isIdle,
        configuration,
        logo,
        getClient,
        isMobileOrTablet,
        logoData,
        backdropCategory,
        comparedProducts,
        compareCount,
        toggleMenuAccount,
        toggleMobileMenu,
        setPriceGross,
        backdropRun,
        onUpdateAnswer,
        onCategoryClick,
        menuAccountClose,
        closeMobileMenuAfterSelected,
        defaultSeoTitleTemplate,
        setHeadTags,
        setLangAttribute,
        setTenantIcons,
        setTenantCss,
        setScripts,
        setPwaAttributes,
      }
    },
    head() {
      const vm = this as any
      const defaultHeader: MetaInfo = {
        title: vm.structuredSeoTitle(DIVIDER.DOT, [vm.defaultSeoData.title]),
        titleTemplate: (titleChunk: string) => vm.defaultSeoTitleTemplate(titleChunk),
        meta: [
          { charset: 'utf-8' },
          { name: 'viewport', content: 'width=device-width, minimum-scale=1, maximum-scale=1' },
          {
            hid: 'description',
            name: 'description',
            content: vm.structuredSeoTitle(DIVIDER.DASH, [vm.defaultSeoData.description]),
          },
          { hid: 'author', name: 'author', content: 'Unity Group' },
          { hid: 'robots', name: 'robots', content: vm.defaultSeoRobots },
        ],
        link: [
          {
            rel: 'icon',
            type: 'image/x-icon',
            href: (vm.logo && vm.logo.favicon) || 'favicon.png',
          },
        ],
        script: [
          vm.breadcrumbs.length ? {
            type: 'application/ld+json',
            json: {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: vm.breadcrumbs.map((item: any, index: number) => {
                return {
                  '@type': 'ListItem',
                  position: index + 1,
                  name: item.name,
                  item: item.link,
                }
              }),
            },
          } : {},
        ],
        noscript: [],
      }

      vm.setLangAttribute(defaultHeader)
      vm.setTenantIcons(defaultHeader)
      vm.setPwaAttributes(defaultHeader)
      vm.setTenantCss(defaultHeader)
      vm.setScripts(defaultHeader)
      vm.setHeadTags(defaultHeader)

      return defaultHeader
    },
  } as any)
